import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    XMarkIcon,
    ShieldExclamationIcon,
    DocumentTextIcon,
    TagIcon,
    ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { Button, Badge } from "../../ui";
import {
    riskImpactScore,
    riskLikelihoodScore,
    riskScoreColor,
} from "../../../constants/AppEnums";

const Field = ({ label, children }) => (
    <div>
        <p className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
            {label}
        </p>
        <div className="text-sm text-gray-900">{children}</div>
    </div>
);

const RiskViewModal = ({ isOpen, onClose, risk }) => {
    if (!risk) return null;

    const getImpactValue = (val) =>
        riskImpactScore.find(i => i.name.toLowerCase() === val?.toLowerCase())?.value ?? "—";

    const getLikelihoodValue = (val) =>
        riskLikelihoodScore.find(i => i.name.toLowerCase() === val?.toLowerCase())?.value ?? "—";

    const getRiskScoreColor = (score) =>
        riskScoreColor.find(i => i.name.toLowerCase() === score?.toLowerCase())?.color ?? "bg-gray-400";

    const statusStyle = {
        open: "bg-red-100 text-red-700",
        mitigated: "bg-green-100 text-green-700",
        closed: "bg-gray-100 text-gray-600",
        "in-progress": "bg-primary-50 text-primary-700",
    }[risk.risk_status?.toLowerCase()] ?? "bg-gray-100 text-gray-600";

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                        onClick={onClose}
                    />

                    {/* Modal Panel */}
                    <motion.div
                        key="modal"
                        initial={{ opacity: 0, y: -24, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.97 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh] overflow-hidden"
                    >
                        {/* Header */}
                        <div className="flex items-center gap-4 px-6 py-5 bg-gradient-to-r from-primary-600 to-primary-500 rounded-t-2xl">
                            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                                <ShieldExclamationIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-white leading-tight">
                                    Risk Details
                                </h3>
                                <p className="text-xs text-primary-100 mt-0.5 font-mono">
                                    {risk.risk_id}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-shrink-0 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto">
                            <div className="px-6 py-5 space-y-5">

                                {/* Description */}
                                <div>
                                    <p className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                                        Risk Description
                                    </p>
                                    <div className="relative">
                                        <DocumentTextIcon className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                                        <p className="pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 leading-relaxed">
                                            {risk.risk_description || "—"}
                                        </p>
                                    </div>
                                </div>

                                {/* Classification */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Framework / Regulation">
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${risk.framework === "General" ? "bg-gray-100 text-gray-600" : "bg-primary-50 text-primary-700"}`}>
                                            {risk.framework_control || risk.framework || "General"}
                                        </span>
                                    </Field>
                                    <Field label="Category">
                                        <span className="text-gray-800">{risk.category || "—"}</span>
                                    </Field>
                                    <Field label="Sub Category">
                                        <span className="text-gray-800">{risk.risk_sub_category || "—"}</span>
                                    </Field>
                                    <Field label="Risk Type">
                                        <span className="text-gray-800">{risk.risk_type || "—"}</span>
                                    </Field>
                                    <Field label="Control Reference">
                                        <span className="font-mono text-gray-800">{risk.framework_control || "N/A"}</span>
                                    </Field>
                                    <Field label="Status">
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle}`}>
                                            {risk.risk_status || "Open"}
                                        </span>
                                    </Field>
                                </div>

                                {/* Risk Scores */}
                                <div>
                                    <p className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                        <ShieldCheckIcon className="w-3.5 h-3.5" />
                                        Risk Scores
                                    </p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl">
                                        <Field label="Inherent Impact">
                                            <Badge variant="secondary" size="sm">
                                                {risk.inherent_impact_type} – {getImpactValue(risk.inherent_impact_type)}
                                            </Badge>
                                        </Field>
                                        <Field label="Inherent Likelihood">
                                            <Badge variant="secondary" size="sm">
                                                {risk.inherent_likelihood_type} – {getLikelihoodValue(risk.inherent_likelihood_type)}
                                            </Badge>
                                        </Field>
                                        <Field label="Inherent Risk Rating">
                                            {risk.inherent_risk_score ? (
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold text-white ${getRiskScoreColor(risk.inherent_risk_score)}`}>
                                                    {risk.inherent_risk_score}
                                                </span>
                                            ) : <span className="text-gray-400">N/A</span>}
                                        </Field>
                                        <Field label="Residual Risk Rating">
                                            {risk.residual_risk_score ? (
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold text-white ${getRiskScoreColor(risk.residual_risk_score)}`}>
                                                    {risk.residual_risk_score}
                                                </span>
                                            ) : <span className="text-gray-400">N/A</span>}
                                        </Field>
                                    </div>
                                </div>

                                {/* Existing Controls */}
                                <div>
                                    <p className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                        <TagIcon className="w-3.5 h-3.5" />
                                        Existing Controls
                                    </p>
                                    <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                        {risk.existing_controls || "None documented"}
                                    </p>
                                </div>

                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-2xl">
                            <p className="text-xs text-gray-400">
                                Created by <span className="font-medium text-gray-600">{risk.created_by || "System"}</span>
                            </p>
                            <Button variant="secondary" size="sm" onClick={onClose}>
                                Close
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default RiskViewModal;
